import { Injectable } from 'angular-ts-decorators';

import { Initable } from './initable';
import { merge, isEmpty, omitBy, pickBy, isBoolean } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { Domain } from './domain';
import { SettingsModel, PartialSettingsModel } from '@classes/settings-model';
import { SettingsOverrides } from '@shared/app/scripts/services/settings-overrides';
import type { IConfig } from './config';

/**
 * Simple service that allows retrieve the settings, expose them to other
 * services & updates on session changes.
 *
 * Please compare with {@link coreModule.settings}.
 */
@Injectable('settingsManager')
export class SettingsManager implements Initable {
  private SESSION_COOKIE: string;
  private settingsSubject: BehaviorSubject<SettingsModel>;

  /**
   * It will contain object compiled from user & tenant settings provided by
   * /settings/get endpoint stay with defaults if the requests fails.
   *
   * Note! It always keeps the same reference; any updates are done at the
   * same object.
   */
  public _settings: SettingsModel;

  /**
   * A stream with will contain an object compiled from user & tenant settings provided by
   * /settings/get endpoint stay with defaults if the requests fails.
   */
  public readonly settings$: Observable<SettingsModel>;

  private settingsDefaults: PartialSettingsModel = {
    vat: {},
    email: {},
    piggyApiToken: '',
    loyalty: {
      cashback: {},
      printPointsOnReceipt: false,
    },
    shippingCarriers: [],
    customLabels: {},
    currencies: [],
    sales: { hiddenFields: {}, readOnlyFields: {}, orderTemplate: {} },
    secondScreen: {},
    paymentMethods: {
      cash: {},
      consignment: {},
      creditCard: {},
      creditReceipt: {},
      pin: {},
      storeCredit: {},
      withdrawal: {},
    },
  };

  constructor(
    private $q: ng.IQService,
    private $http: ng.IHttpService,
    private ipCookie: any,
    private domain: Domain,
    private config: IConfig,
    private settingsOverrides: SettingsOverrides,
  ) {
    'ngInject';

    // TODO move to shared constant
    this.SESSION_COOKIE = 'sid';
    this.settingsSubject = new BehaviorSubject(
      angular.copy(this.settingsDefaults) as SettingsModel,
    );

    this.settings$ = this.settingsSubject.asObservable();
    this._settings = angular.copy(this.settingsDefaults) as SettingsModel;
  }

  get settings() {
    return this._settings;
  }

  /**
   * Start settings initialization - can be run multiple times; every time on
   * success notify observers registered with {@link
   * sharedModule.settingsManager#onSuccess settingsManager.onSuccess}
   * @returns {promise}
   * it will resolve with object compiled from user & tenant settings provided
   * by /settings/get endpoint or reject with error
   *
   * # Note!
   * With the current implementation it will break if it's called more then once!
   */
  init(): ng.IPromise<any> {
    let baseUrl: string;
    const deferred = this.$q.defer();

    if (this.ipCookie(this.SESSION_COOKIE)) {
      baseUrl = '//' + this.domain.createSibling(this.config.spynlSubdomain);
      // TODO consider moving to separate service
      this.$http
        .get(baseUrl + '/settings/get', {
          // done on config level for v7; setting for v6
          withCredentials: true,
        })
        .then(
          (response: any) => {
            const data = response.data.data;

            angular.copy(this.settingsDefaults, this._settings);

            // combine user, tenant & default settings, in that order of
            // priorities
            this._settings = merge(
              data.tenant.settings,
              omitBy(data.user.settings, isEmpty),
              pickBy(data.user.settings, isBoolean),
              this.settingsOverrides,
            );

            deferred.resolve(this._settings);
            this.settingsSubject.next(angular.copy(this._settings));
          },
          (error) => {
            this._settings = angular.copy(
              this.settingsDefaults as SettingsModel,
            );
            if (error && angular.isObject(error.data)) {
              deferred.reject(error.data.message);
            } else {
              deferred.reject('Cannot loads settings');
            }
          },
        );
    } else {
      deferred.reject('Session cookie is not available');
    }

    return deferred.promise;
  }
}
